import { Anchor, Box, Flex, Group, Image, Text } from '@mantine/core'
import { type DataTableColumn } from 'mantine-datatable'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import PriceChange from '@/components/PriceChange'
import type { EquityBasketOffer } from '@/services/equity/baskets'
import { getCdcWebSignupLink } from '@/utils'

import styles from '../styles.module.css'

// Placeholder for a missing value (same as the stocks table): an em dash in
// the secondary content color.
function NaPlaceholder() {
  return (
    <Text component="span" c="var(--content-secondary)">
      —
    </Text>
  )
}

// Max stacked asset logos per basket, then a "+N" badge (mirrors cdc-web
// WhaleBasketRow's CoinIconList).
const MAX_STACKED_LOGOS = 5

function AssetLogoStack({ offer }: { readonly offer: EquityBasketOffer }) {
  const t = useTranslations('stockTable.baskets')
  const assets = offer.assets ?? []
  if (assets.length === 0) return <NaPlaceholder />

  const visible = assets.slice(0, MAX_STACKED_LOGOS)
  const overflow = assets.length - visible.length

  return (
    <Group gap={0} wrap="nowrap">
      {visible.map((asset, index) => (
        <Image
          key={asset.asset_id || asset.display_symbol || index}
          src={asset.icon_url ?? undefined}
          alt={asset.display_symbol}
          title={asset.display_symbol}
          w={20}
          h={20}
          radius="50%"
          // Overlap the stack like cdc-web CoinIconList (me={-4}).
          style={{ marginLeft: index === 0 ? 0 : -4 }}
          fallbackSrc=""
        />
      ))}
      {overflow > 0 && (
        <Text component="span" variant="body" c="var(--content-secondary)" ml={6}>
          {t('moreAssets', { count: overflow })}
        </Text>
      )}
    </Group>
  )
}

function BasketColumn({ offer }: { readonly offer: EquityBasketOffer }) {
  const t = useTranslations('stockTable.baskets')
  const assetCount = offer.assets?.length ?? 0
  // 64px rounded banner (desktop) / 32px (mobile) + title, visuals ported from
  // cdc-web WhaleBasketRow. On mobile the row collapses into a card mirroring
  // the crypto baskets list — banner, title + constituent count, and the 1Y
  // change pinned right — so it never needs side-to-side swiping (WWW-5114).
  return (
    <div className={styles.mobileBasketRow}>
      <Group gap="12px" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
        <Box
          className={styles.basketImage}
          style={{
            // Placeholder colour uses the backgroundColor longhand, NOT Mantine's
            // `bg` prop: `bg` maps to the `background` shorthand, which resets
            // background-image and would wipe the banner below (root cause of the
            // all-grey cells).
            backgroundColor: 'var(--bg-surface-emphasis)',
            backgroundImage: offer.banner_url ? `url("${encodeURI(offer.banner_url)}")` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
        {/* Desktop-only title. Bounded wrap (not truncation): cap the width so
            the column doesn't eat the table's free width and push the Stocks
            column far right (WWW-5115); long titles wrap on a second line. */}
        <Text
          variant="body"
          c="var(--content-primary)"
          maw={{ base: '160px', midScale: '240px' }}
          title={offer.title}
          className={styles.desktopBasketTitle}
          style={{ wordBreak: 'break-word' }}
        >
          {offer.title}
        </Text>
        {/* Mobile-only stacked title + constituent count; long names wrap
            instead of overflowing (ticket feedback 2026-09-10). */}
        <div className={styles.mobileBasketInfo}>
          <Text variant="body1" c="var(--content-primary)" style={{ wordBreak: 'break-word' }}>
            {offer.title}
          </Text>
          <span className={styles.mobileCoinsText}>{t('assetCount', { count: assetCount })}</span>
        </div>
      </Group>
      <div className={styles.mobilePriceChange}>
        <YearPerformanceColumn offer={offer} />
      </div>
    </div>
  )
}

function YearPerformanceColumn({ offer }: { readonly offer: EquityBasketOffer }) {
  const percentage = offer.performance?.percentage
  if (percentage === null || percentage === undefined) return <NaPlaceholder />
  // Upstream `percentage` is already a percentage number (e.g. "10.47" =
  // +10.47%), but PriceChange expects a decimal fraction.
  const asNumber = Number(percentage)
  if (!Number.isFinite(asNumber)) return <NaPlaceholder />
  return <PriceChange value={asNumber / 100} />
}

function TradeColumn() {
  const t = useTranslations('coinTable.row')
  // "Trade" text link → cdc-web signup（与整行点击同一目标，行为同 crypto
  // basket 先例）。
  return (
    <Anchor href={getCdcWebSignupLink()} onClick={e => e.stopPropagation()} underline="never">
      <Text variant="subtitle-emphasis" c="var(--content-active)" style={{ wordBreak: 'keep-all' }}>
        {t('trade')}
      </Text>
    </Anchor>
  )
}

export function useBasketColumns() {
  const t = useTranslations('stockTable.baskets')
  return useMemo<DataTableColumn<EquityBasketOffer>[]>(
    () => [
      {
        accessor: 'title',
        title: t('headers.basket'),
        cellsClassName: styles.defaultCell,
        render: offer => <BasketColumn offer={offer} />,
      },
      {
        accessor: 'assets',
        title: t('headers.stocks'),
        // Centered in its column, mirroring the crypto basket table's Coins
        // middle column (WWW-5144).
        textAlign: 'center',
        render: offer => (
          <Flex justify="center">
            <AssetLogoStack offer={offer} />
          </Flex>
        ),
      },
      // Header is fixed "1 Year": the upstream performance field is always the
      // 1-year percentage (decision WWW-5085 Q8, 2026-09-07). On mobile the
      // whole header row (and this column's body) is hidden by CSS — the value
      // renders inside the basket card — so the long form shows from tablet up
      // (QA clarification on WWW-5152, 2026-09-18).
      {
        accessor: 'performance',
        title: t('headers.oneYear'),
        textAlign: 'right',
        render: offer => (
          <Flex justify="flex-end">
            <YearPerformanceColumn offer={offer} />
          </Flex>
        ),
      },
      {
        accessor: 'action',
        title: '',
        textAlign: 'center',
        width: '0%',
        render: () => <TradeColumn />,
      },
    ],
    [t],
  )
}
